import { z } from 'zod';
/**
 * D'où vient une anomalie, demandé par le test terrain du 25/08 :
 *
 * > « En cas de création d'une anomalie — Provenance : Étage (indiquer l'étage : RDC, 1er,
 * > etc.) ; Réseau d'assainissement public : Exutoire inconnu. »
 *
 * ⚠️ Le mot métier est « provenance », mais `Anomaly.provenance` est **déjà pris** : c'est un
 * code gelé de la liste de vocabulaire du même nom (M17, M19), qui dit la localisation de
 * l'équipement (« du garage », « de l'étage »). Ce champ-ci répond à la question restée sans
 * réponse — *quel* étage —, et ajoute une provenance que le vocabulaire ne peut pas porter :
 * le réseau public, qui n'est pas dans le bâtiment. D'où un nom distinct, `location`.
 *
 * ⚠️ **Plus rien ne la saisit depuis #114** : la colonne « Provenance » du constructeur, que GHP
 * administre, dit l'étage et le sous-sol, et les pastilles de la tablette ont été retirées. Le
 * champ reste pour ce qui est déjà relevé — en base, dans un brouillon d'avant — et se lit au
 * bureau et au rapport (`anomalyLocationLabel`).
 *
 * **Une union discriminée, pas deux champs nullables**, parce que les deux provenances ne sont
 * pas de même nature : l'une localise dans le bâtiment et exige un étage, l'autre désigne le
 * réseau d'assainissement public et n'a pas d'étage. Deux colonnes libres laisseraient écrire
 * « 2ᵉ étage du réseau public », que rien ne saurait relire.
 */
export const BUILDING_LEVELS = ['ground', 'first', 'second', 'third'];
export const buildingLevelSchema = z.enum(BUILDING_LEVELS);
/**
 * La seule valeur que le retour terrain nomme. Un exutoire identifié se désignera par une
 * **seconde valeur**, pas par une seconde forme — c'est ce que cette énumération réserve.
 */
export const PUBLIC_NETWORK_OUTLETS = ['unknown'];
export const publicNetworkOutletSchema = z.enum(PUBLIC_NETWORK_OUTLETS);
/**
 * `looseObject` de part et d'autre, pour la raison de M16 : une tablette peut porter une
 * version plus récente que l'API le temps d'un déploiement, et valider en `strict` reviendrait
 * à effacer une fiche que personne ne pourra ressaisir.
 */
export const anomalyLocationSchema = z.discriminatedUnion('kind', [
    z.looseObject({ kind: z.literal('building_level'), level: buildingLevelSchema }),
    z.looseObject({ kind: z.literal('public_network'), outlet: publicNetworkOutletSchema }),
]);
const BUILDING_LEVEL_LABELS = {
    ground: 'RDC',
    first: '1er étage',
    second: '2e étage',
    third: '3e étage',
};
const PUBLIC_NETWORK_OUTLET_LABELS = {
    unknown: 'Réseau public : exutoire inconnu',
};
/**
 * La provenance d'une anomalie enregistrée, en français — au bureau comme au rapport.
 *
 * Prend un `unknown` parce que la colonne est un `jsonb` : ce qui ne se relit pas ne se dit pas,
 * et ne fait pas tomber l'écran ni le rapport (M21). `null` : l'anomalie n'en porte pas.
 */
export function anomalyLocationLabel(value) {
    const location = anomalyLocationSchema.safeParse(value).data;
    if (location === undefined)
        return null;
    return location.kind === 'building_level'
        ? BUILDING_LEVEL_LABELS[location.level]
        : PUBLIC_NETWORK_OUTLET_LABELS[location.outlet];
}
//# sourceMappingURL=anomaly-location.js.map